import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { X, Send, Tv } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../lib/api/client';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface RequestSeriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery?: string;
}

export function RequestSeriesModal({ isOpen, onClose, searchQuery = '' }: RequestSeriesModalProps) {
  const [title, setTitle] = useState(searchQuery);
  const [imdbUrl, setImdbUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Informe o título da série');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/streaming/requests', {
        title: title.trim(),
        imdb_url: imdbUrl.trim() || null,
        description: description.trim() || null,
      });

      toast.success('✅ Série solicitada com sucesso!');
      setTitle('');
      setImdbUrl('');
      setDescription('');
      onClose();
    } catch (error: any) {
      console.error('Erro ao solicitar série:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao solicitar série');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <View style={styles.overlay}>
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerTitle}>
              <Tv size={24} color="#ff6b00" style={styles.iconMargin} />
              <Text style={styles.title}>Solicitar Série</Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              disabled={isSubmitting}
              style={styles.closeButton}
            >
              <X size={24} color={NETFLIX_COLORS.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Description */}
          <Text style={styles.description}>
            Não encontrou a série? Nos ajude a melhorar sugerindo novos conteúdos!
          </Text>

          {/* Form Fields */}
          <View style={styles.formGroup}>
            {/* Title */}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Título da série *</Text>
              <TextInput
                style={[styles.input, isSubmitting && styles.inputDisabled]}
                placeholder="Ex: Squid Game"
                placeholderTextColor={NETFLIX_COLORS.textTertiary}
                value={title}
                onChangeText={setTitle}
                editable={!isSubmitting}
              />
            </View>

            {/* IMDb URL */}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Link IMDb ou TMDB (opcional)</Text>
              <TextInput
                style={[styles.input, isSubmitting && styles.inputDisabled]}
                placeholder="https://imdb.com/title/..."
                placeholderTextColor={NETFLIX_COLORS.textTertiary}
                value={imdbUrl}
                onChangeText={setImdbUrl}
                editable={!isSubmitting}
              />
            </View>

            {/* Description */}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Descrição (opcional)</Text>
              <TextInput
                style={[styles.textarea, isSubmitting && styles.inputDisabled]}
                placeholder="Conte um pouco sobre a série..."
                placeholderTextColor={NETFLIX_COLORS.textTertiary}
                value={description}
                onChangeText={setDescription}
                editable={!isSubmitting}
                multiline
                numberOfLines={4}
              />
            </View>
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton, isSubmitting && styles.buttonDisabled]}
              onPress={onClose}
              disabled={isSubmitting}
            >
              <Text style={styles.buttonText}>Cancelar</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.submitButton,
                (isSubmitting || !title.trim()) && styles.buttonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? (
                <ActivityIndicator color="white" size="small" />
              ) : (
                <>
                  <Send size={16} color="white" style={styles.sendIcon} />
                  <Text style={styles.buttonText}>Solicitar</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as any,
  container: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '90vh',
    backgroundColor: NETFLIX_COLORS.surfaceHover,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
  } as any,
  scrollContent: {
    padding: NETFLIX_SPACING.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: NETFLIX_SPACING.lg,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
    marginLeft: NETFLIX_SPACING.sm,
  },
  closeButton: {
    padding: NETFLIX_SPACING.sm,
    marginLeft: NETFLIX_SPACING.md,
  },
  description: {
    fontSize: 14,
    color: NETFLIX_COLORS.textSecondary,
    marginBottom: NETFLIX_SPACING.lg,
    lineHeight: 20,
  },
  formGroup: {
    gap: NETFLIX_SPACING.md,
    marginBottom: NETFLIX_SPACING.lg,
  },
  fieldContainer: {
    marginBottom: NETFLIX_SPACING.md,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: NETFLIX_COLORS.textSecondary,
    marginBottom: NETFLIX_SPACING.sm,
  },
  input: {
    width: '100%',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: NETFLIX_SPACING.sm,
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  textarea: {
    width: '100%',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: NETFLIX_SPACING.sm,
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
    textAlignVertical: 'top',
    minHeight: 100,
  } as any,
  inputDisabled: {
    opacity: 0.6,
  },
  actions: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
    marginTop: NETFLIX_SPACING.lg,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: NETFLIX_SPACING.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  cancelButton: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  submitButton: {
    backgroundColor: '#ff6b00',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
  },
  iconMargin: {
    marginRight: 8,
  },
  sendIcon: {
    marginRight: 6,
  },
});
